import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { SerializedReel } from '../../types';
import { reelMessages, reelVoiceMessages } from './reelMessages';
import { reelReviewMessages } from './reelReviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelSummaryProps {
  summary: NonNullable<SerializedReel['summary']>;
  voiceId: SerializedReel['voiceId'];
  /** Shows the "You can change it when you approve" hint (status ready). */
  canEditCaption: boolean;
}

const SectionHead: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className={styles.secHead}>
    <h3 className={styles.secTitle}>{title}</h3>
    {hint !== undefined && <span className={styles.secHint}>{hint}</span>}
  </div>
);

/**
 * What the AI wrote (design c1): the captions in order (the API gives no times, so the list
 * shows numbers), the voiceover script and the suggested post caption.
 */
const ReelSummaryComponent: React.FC<ReelSummaryProps> = ({ summary, voiceId, canEditCaption }) => {
  const intl = useIntl();

  const captions = useMemo(
    () =>
      summary.captions.map((caption, index) => (
        // eslint-disable-next-line react/no-array-index-key -- the captions are an ordered list; two can be equal
        <li key={`caption-${index}`} className={styles.cap}>
          <span className={styles.capNumber}>{String(index + 1).padStart(2, '0')}</span>
          <span>{caption}</span>
        </li>
      )),
    [summary.captions]
  );

  const voiceHint =
    voiceId === null
      ? undefined
      : intl.formatMessage(reelReviewMessages.voiceHint, {
          voice: intl.formatMessage(reelVoiceMessages[voiceId].label),
          tone: intl.formatMessage(reelVoiceMessages[voiceId].tone),
        });

  return (
    <>
      {summary.captions.length > 0 && (
        <div>
          <SectionHead title={intl.formatMessage(reelReviewMessages.captions)} hint={String(summary.captions.length)} />
          <ol className={styles.caps}>{captions}</ol>
        </div>
      )}
      <div>
        <SectionHead title={intl.formatMessage(reelReviewMessages.voiceover)} hint={voiceHint} />
        <p className={styles.script}>{summary.voiceoverScript ?? intl.formatMessage(reelMessages.noVoiceover)}</p>
      </div>
      {summary.caption !== '' && (
        <div>
          <SectionHead
            title={intl.formatMessage(reelReviewMessages.postCaption)}
            hint={canEditCaption ? intl.formatMessage(reelReviewMessages.postCaptionHint) : undefined}
          />
          <p className={clsx(styles.script, styles.scriptPlain)}>{summary.caption}</p>
        </div>
      )}
    </>
  );
};

export const ReelSummary = TypedMemo(ReelSummaryComponent);
